/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, Ref, ComponentProps } from 'preact';
import { forwardRef } from 'preact/compat';

import { BareButton } from '#UNSAFE_BareButton';
import { StyledCard } from '#PRIVATE_StyledCard';

import { type ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { type TestIdProps } from '#hooks/UNSAFE_useTestId';
import { type AriaProps } from '#utils/UNSAFE_interpolations/aria';
import { type DimensionProps } from '#utils/UNSAFE_interpolations/dimensions';
import { type PaddingProps } from '#utils/UNSAFE_interpolations/padding';
import { useActionable } from '#hooks/UNSAFE_useActionable';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';

type StyleProps = DimensionProps & PaddingProps & ColorProps & AriaProps;

type Props = StyleProps &
  TestIdProps & {
    children?: ComponentChildren;

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: ComponentProps<typeof BareButton>['onAction'];
  };

/**
 * An action card is a container that renders a group of related information in a styled rectangular area, provides enhanced styling for hover and focus states, and invokes an action event when clicked.
 * Width, height, padding, and background color are set through props.
 */
export const ActionCard = forwardRef(
  ({ children, onAction, testId, ...rest }: Props, ref: Ref<HTMLDivElement>) => {
    const { isTabbable } = useTabbableMode();

    const pressHandler = (e: Event) => {
      if (onAction) {
        onAction({
          reason: e.type == 'keyup' ? 'keyboard' : 'pointer'
        });
      }
    };

    const { actionableProps, isHover, isActive } = useActionable(pressHandler);

    return (
      <StyledCard
        {...actionableProps}
        cursor="pointer"
        display="inlineFlex"
        isActive={isActive}
        isHover={isHover}
        parentComponentVariant="actionCard"
        ref={ref}
        role="button"
        tabIndex={isTabbable ? 0 : -1}
        testId={testId}
        {...rest}>
        {children}
      </StyledCard>
    );
  }
);
